import { createFilterStore } from 'shared/state/create-filter-store';
import { Animal, BreedType, Gender, PetAge } from 'shared/@generated/models';
import markets from 'shared/@generated/markets.json';
import { sanitizeQueryParams } from 'shared/library/api-client';

export type ListingsFilter = {
  animal: Animal | null;
  breeds: (number | string)[] | null;
  breedType: BreedType | null;
  gender: Gender | null;
  petAge: PetAge | null;
  onlyBreeders: boolean | null;
  maxPrice: number | null;
  maxDistanceInKm: number | null;
  latitude: number | null;
  longitude: number | null;
  country: string | null;
  sortBy: string | null;
  page: number;
};

export const defaultListingsFilter: ListingsFilter = {
  animal: null,
  breeds: null,
  breedType: null,
  gender: null,
  petAge: null,
  onlyBreeders: null,
  maxPrice: null,
  maxDistanceInKm: null,
  latitude: null,
  longitude: null,
  country: null,
  sortBy: null,
  page: 1,
};

export const sortOptions = ['recommended', 'new', 'old', 'cheap', 'expensive', 'popular'];

const listingFilterAliases: Partial<Record<string, keyof ListingsFilter>> = {
  breed_type: 'breedType',
  max_distance_in_km: 'maxDistanceInKm',
  max_price: 'maxPrice',
  only_breeders: 'onlyBreeders',
  pet_age: 'petAge',
  sort_by: 'sortBy',
};

const countryCodes = new Set(markets.map((market) => market.country_code));

function isEnumValue<T extends string>(values: Record<string, T>, value: unknown): value is T {
  return Object.values(values).includes(value as T);
}

function validNumber(value: unknown, { min, max }: { min: number; max: number }): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(number) && number >= min && number <= max ? number : null;
}

function normalizeBreeds(value: ListingsFilter['breeds']): ListingsFilter['breeds'] {
  if (!Array.isArray(value)) {
    return null;
  }

  const breeds = value.filter(
    (breed): breed is number | string =>
      (typeof breed === 'number' && Number.isInteger(breed) && breed > 0) ||
      (typeof breed === 'string' && breed.trim().length > 0),
  );

  return breeds.length > 0 ? breeds : null;
}

export function sanitizeListingsFilterParams(
  values: Partial<ListingsFilter> & Record<string, unknown>,
): ListingsFilter {
  const canonicalValues: Partial<ListingsFilter> = {};

  for (const key of Object.keys(defaultListingsFilter) as (keyof ListingsFilter)[]) {
    if (values[key] !== undefined) {
      canonicalValues[key] = values[key] as never;
    }
  }

  for (const [alias, key] of Object.entries(listingFilterAliases)) {
    if (canonicalValues[key!] === undefined && values[alias] !== undefined) {
      canonicalValues[key!] = values[alias] as never;
    }
  }

  const filter = sanitizeQueryParams(canonicalValues, defaultListingsFilter, ['breeds']);
  const latitude = validNumber(filter.latitude, { min: -90, max: 90 });
  const longitude = validNumber(filter.longitude, { min: -180, max: 180 });
  const maxDistanceInKm = validNumber(filter.maxDistanceInKm, { min: 1, max: 200 });
  const hasCompleteLocation = latitude !== null && longitude !== null && maxDistanceInKm !== null;

  return {
    animal: isEnumValue(Animal, filter.animal) ? filter.animal : null,
    breeds: normalizeBreeds(filter.breeds),
    breedType: isEnumValue(BreedType, filter.breedType) ? filter.breedType : null,
    gender: isEnumValue(Gender, filter.gender) ? filter.gender : null,
    petAge: isEnumValue(PetAge, filter.petAge) ? filter.petAge : null,
    onlyBreeders: filter.onlyBreeders === true ? true : null,
    maxPrice: validNumber(filter.maxPrice, { min: 1, max: 99999 }),
    maxDistanceInKm: hasCompleteLocation ? maxDistanceInKm : null,
    latitude: hasCompleteLocation ? latitude : null,
    longitude: hasCompleteLocation ? longitude : null,
    country:
      typeof filter.country === 'string' && countryCodes.has(filter.country)
        ? filter.country
        : null,
    sortBy:
      typeof filter.sortBy === 'string' && sortOptions.includes(filter.sortBy)
        ? filter.sortBy
        : null,
    page:
      typeof filter.page === 'number' && Number.isInteger(filter.page) && filter.page >= 1
        ? filter.page
        : 1,
  };
}

function sameFilterValue(left: unknown, right: unknown) {
  return Array.isArray(left) || Array.isArray(right)
    ? JSON.stringify(left) === JSON.stringify(right)
    : left === right;
}

export function getListingsFilterQuery(
  filter: ListingsFilter,
  routeDefaults: Partial<ListingsFilter> = {},
): Record<string, string | number> {
  const query: Record<string, string | number> = {};

  for (const key of Object.keys(defaultListingsFilter) as (keyof ListingsFilter)[]) {
    const value = filter[key];

    if (key === 'page' && value === 1) {
      continue;
    }

    if (key in routeDefaults && sameFilterValue(value, routeDefaults[key])) {
      continue;
    }

    if (value === null || value === undefined || value === false || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        query[key] = value.join(',');
      }

      continue;
    }

    query[key] = String(value);
  }

  return query;
}

function hasActiveFilterValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== null && value !== undefined && value !== false && value !== '';
}

export function shouldNoIndexListingsFilter(
  filter: ListingsFilter,
  ignoredKeys: (keyof ListingsFilter)[] = [],
) {
  return (Object.keys(defaultListingsFilter) as (keyof ListingsFilter)[]).some((key) => {
    if (key === 'page' || ignoredKeys.includes(key)) {
      return false;
    }

    return hasActiveFilterValue(filter[key]);
  });
}

export const [ListingsFilterProvider, useListingsFilterStore] =
  createFilterStore(defaultListingsFilter);
